import { guid } from "../util/Guid"

import { servicesAPI } from "./api"

import Button = ContextMenu.Button
import CallButton = ContextMenu.CallButton

const AttachmentsKey = "Attachments"
const AttachmentsEditableKey = "AreAttachmentsEditable"
const AttachmentsEnabledKey = "AreAttachmentsEnabled"
const IsNumberEnabledKey = "IsNumberEnabled"
const ItemIdKey = "ItemId"
const ListIdKey = "ListId"
const ListNameKey = "ListName"
const NumberKey = "Nr"
const StatusKey = "Status"
const TitleKey = "Title"
const WorkspaceIdKey = "WorkspaceId"

export const getCardViewData = (
    selectionFilterId: string,
    pageIndex = 0,
    smartListContext = -1,
    pageSize: number,
): Promise<Models.CollectionView> => {
    const payload = {
        selectionFilterId,
        pageIndex,
        smartListContext,
        pageSize,
    }

    return servicesAPI.postJSON(
        "GetItemBoxDataForCardView",
        payload,
        parseCardViewData,
    )
}

export const getCardViewItems = (
    selectionFilterId: string,
    itemIds: number[],
): Promise<Models.Item[]> => {
    const payload = {
        selectionFilterId,
        itemIds,
    }

    return servicesAPI.postJSON(
        "CollectItemsForCardView",
        payload,
        parseCardViewItems,
    )
}

export const getCardViewItemActions = (itemId: number): Promise<Button[]> => {
    const payload = {
        itemId,
    }

    return servicesAPI.postJSON(
        "GetItemActionsForCardView",
        payload,
        parseItemActionsData,
    )
}

export const executeCardViewItemAction = (
    itemId: number,
    action: string,
): Promise<string> => {
    const payload = {
        itemid: `${itemId}i`,
        action,
    }

    return servicesAPI.postJSON(
        "ExecuteCardViewItemAction",
        payload,
        parseExecuteCardViewItemActionData,
    )
}

const parseCardViewData = (
    json: Api.GetItemBoxDataResponseWrapper,
): Models.CollectionView => {
    const {
        d: {
            Value: { PagingInfo, Items, allowADD },
        },
    } = json
    if (!PagingInfo) {
        throw new Error(`no pagingInfo in itemBoxResponse: ${json}`)
    }
    if (!Items) {
        throw new Error(`no items in itemBoxResponse: ${json}`)
    }
    if (allowADD === undefined) {
        throw new Error(`no allowADD in itemBoxResponse: ${json}`)
    }

    const paging = {
        currentPage: PagingInfo.SelectedPageIndex,
        totalNumberOfItems: PagingInfo.TotalNumberOfItems,
        pageSize: PagingInfo.ItemsPerPage,
    }

    return { paging, items: Items.map(parseItem), allowAdd: allowADD }
}

const parseCardViewItems = (
    json: Api.GetItemsResponseWrapper,
): Models.Item[] => {
    const {
        d: { Value: Items },
    } = json

    if (!Items) {
        throw new Error(`no items in itemBoxResponse: ${json}`)
    }

    return Items.map(parseItem)
}

const parseItem = (responseItem: Api.ItemBoxItem): Models.Item => {
    const { Properties } = responseItem

    const properties = {} as Record<string, unknown>
    Properties.forEach((p) => {
        properties[p.Name] = p.Value
    })

    const itemId = tryGetItemProperty<number>(ItemIdKey)
    const isNumberEnabled = tryGetItemProperty<boolean>(IsNumberEnabledKey)
    const listId = tryGetItemProperty<number>(ListIdKey)
    const listName = tryGetItemProperty<string>(ListNameKey)
    const workspaceId = tryGetItemProperty<number>(WorkspaceIdKey)
    const status = tryGetItemProperty<string>(StatusKey)
    const title = tryGetItemProperty<string>(TitleKey)
    const linkedDocuments = tryGetItemProperty<[]>(AttachmentsKey)
    const areAttachmentsEditable = tryGetItemProperty<boolean>(
        AttachmentsEditableKey,
    )
    const areAttachmentsEnabled = tryGetItemProperty<boolean>(
        AttachmentsEnabledKey,
    )

    let number
    if (isNumberEnabled) {
        number = tryGetItemProperty<string>(NumberKey)
    }

    return {
        itemId,
        listId,
        listName,
        workspaceId,
        title,
        number,
        status,
        areAttachmentsEditable,
        areAttachmentsEnabled,
        linkedDocuments,
    }

    function tryGetItemProperty<T>(propertyKey: string): T {
        const rawProperty = properties[propertyKey]
        if (typeof rawProperty === "undefined") {
            console.error(
                `item without ${propertyKey} property: ${responseItem}`,
            )
        }
        return rawProperty as T
    }
}

const parseItemActionsData = (json: Api.ServicesResponse<string>): Button[] => {
    const parsedResponse: Api.ItemActionsData<Api.ItemActionsDataValue> =
        JSON.parse(json.d)
    const actions = parsedResponse.value.actions

    if (!actions) {
        throw new Error(`no actions in itemActions response: ${json}`)
    }

    return actions.map((a: CallButton) => {
        const buttonData = {
            type: a.buttonData.type,
            actionId: a.buttonData.actionId,
            callData: a.buttonData.callData,
        }
        return {
            uuid: guid(),
            id: a.id,
            caption: a.caption,
            icon: a.icon,
            tooltip: a.tooltip,
            isLoading: false,
            isCustom: a.isCustom,
            inOptionalMenu: a.inOptionalMenu,
            showCaption: a.showCaption,
            buttonData,
        }
    })
}

const parseExecuteCardViewItemActionData = (
    json: Api.ServicesResponse<string>,
): string => {
    const parsedResponse: Api.ItemActionsData<string> = JSON.parse(json.d)
    return parsedResponse.value
}
